import { PropsWithChildren, useEffect, useState } from 'react';

import { createPortal } from 'react-dom';

import { useHeaderSearch } from '../../Search/HeaderSearchProvider';

/**
 * Portals a search component into the header area.
 *
 * This component handles the dynamic rendering of search into the header by:
 * 1. Tracking portal usage state for fallback rendering
 * 2. Finding and observing the portal target element
 * 3. Managing portal lifecycle and cleanup
 *
 * @example
 * <HeaderSearchPortal>
 *   <SearchComponent />
 * </HeaderSearchPortal>
 */
export const HeaderSearchPortal: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { setPortalInUse } = useHeaderSearch();
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  // Notify the header search context when this portal is active
  // This allows the header to know whether to show its own search input
  useEffect(() => {
    setPortalInUse(true);
    return () => setPortalInUse(false);
  }, [setPortalInUse]);

  // Set up DOM observation to handle dynamic header rendering
  // We need this because:
  // 1. The header might not be immediately available in the DOM
  // 2. The portal target might be added/removed during route changes
  // 3. The portal target will move when moving between mobile and desktop
  // 4. We need to handle hot reloading during development
  useEffect(() => {
    const parentElement = document.querySelector('.header-wrapper');
    if (!parentElement) {
      return;
    }

    const findRoot = () => {
      const root = document.getElementById('logged-in-header-search');
      if (root) {
        setPortalRoot(root);
        return true;
      }
      return false;
    };

    // Try to find the portal target immediately
    findRoot();

    // Set up an observer to watch for DOM changes
    // This ensures we can find the portal target even if it's added later
    const observer = new MutationObserver(() => {
      findRoot();
    });

    observer.observe(parentElement, {
      childList: true,
      subtree: true,
    });

    // Clean up the observer when the component unmounts
    return () => {
      observer.disconnect();
    };
  }, []); // Empty deps array since we want a single observer watching for DOM changes

  // Don't render anything until we have a valid portal target
  if (!portalRoot) {
    return null;
  }

  return createPortal(children, portalRoot);
};
